import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box, Heading, Select } from "@chakra-ui/react";
import { ContactTimelineData } from "../../../api/clientUser/getContactTimeline";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ReachChartProps {
  data: ContactTimelineData[];
  timeFrame: "day" | "month" | "year";
  onTimeFrameChange: (timeFrame: "day" | "month" | "year") => void;
}

export const ReachChart = ({ data, timeFrame, onTimeFrameChange }: ReachChartProps) => {

  const processData = (
    data: ContactTimelineData[],
    timeFrame: string
  ): { labels: string[]; data: number[] } => {
    // API already returns aggregated data by timeFrame
    // Just format labels and calculate cumulative sums
    const labels = data.map(item => {
      const date = new Date(item.date);
      switch (timeFrame) {
        case "day":
          return date.toLocaleDateString();
        case "month":
          return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        case "year":
          return date.getFullYear().toString();
        default:
          return item.date;
      }
    });

    // Calculate cumulative sums
    const cumulativeData: number[] = [];
    let sum = 0;
    data.forEach(item => {
      sum += item.count;
      cumulativeData.push(sum);
    });

    return { labels, data: cumulativeData };
  };

  const { labels, data: chartData } = processData(data, timeFrame);

  const dataConfig = {
    labels,
    datasets: [
      {
        label: "Total Contacts Reached",
        data: chartData,
        fill: true,
        backgroundColor: "rgba(0, 200, 81, 0.1)",
        borderColor: "#00C851",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#00C851",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          font: {
            size: 14,
            weight: "bold",
          },
          color: "#1A202C",
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: "rgba(26, 32, 44, 0.95)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "#00C851",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: "#718096",
        },
      },
      y: {
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 12,
          },
          color: "#718096",
        },
      },
    },
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 8 }}
      borderRadius={{ base: "16px", md: "24px" }}
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px solid"
      borderColor="gray.100"
      overflow="hidden"
      maxW="100%"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" color="#1A202C" fontWeight="700">
          Total Reach Over Time
        </Heading>
        <Select
          id="timeFrameSelector"
          value={timeFrame}
          onChange={(e) => onTimeFrameChange(e.target.value as "day" | "month" | "year")}
          size="md"
          maxW="150px"
          borderRadius="8px"
          focusBorderColor="#00C851"
          fontWeight="600"
        >
          {["day", "month", "year"].map((time) => (
            <option key={time} value={time}>
              {time.charAt(0).toUpperCase() + time.slice(1)}
            </option>
          ))}
        </Select>
      </Box>
      <Line data={dataConfig} options={options} />
    </Box>
  );
};
